import classNames from 'classnames';
import styles from './ImageMarquee.module.css';
import { Picture, Text } from '@farmersdog/corgi-x';
import type { PictureSources } from '@farmersdog/corgi-x';

interface MarqueeItemProps {
  index: number;
  name: string;
  state: string;
  sources: PictureSources[];
}

export interface ImageMarqueeProps {
  /**The direction of the marquee.*/
  direction: 'left' | 'right' | 'up' | 'down';
  /** The speed of the marquee in pixels per second.*/
  speed?: number;
  /** The classname for the marquee.*/
  className?: string;
  /** The items to display in the marquee.*/
  items: MarqueeItemProps[];
  /** Whether the marquee is paused.*/
  isPaused?: boolean;
}

const MarqueeItem = ({ name, state, sources, index }: MarqueeItemProps) => {
  return (
    <div className={styles.imageContainer} data-index={`${name}-${index}`}>
      <div className={styles.imageTagContainer}>
        <Text className={styles.imageTag} color="white" bold>
          {name}
        </Text>
        <Text className={styles.imageTag} color="white">
          {' | '}
          {state}
        </Text>
      </div>
      <Picture
        sources={sources}
        aria-hidden
        alt=""
        className={styles.image}
        key={`picture-${name}-${index}`}
        loading="lazy"
      />
    </div>
  );
};

/**
 * The ImageMarquee component creates a continuous scrolling display of images using pure CSS.
 */
export function ImageMarquee({
  direction = 'left',
  speed = 30,
  className,
  items = [],
  isPaused = false,
}: ImageMarqueeProps) {
  const isVertical = direction === 'up' || direction === 'down';

  // Calculate animation duration based on speed and number of items
  const baseDuration = 30; // Base duration in seconds
  const calculatedDuration = Math.max(10, baseDuration * (45 / speed));

  // Determine animation name based on direction
  const animationName = isVertical
    ? direction === 'up'
      ? styles.scrollUp
      : styles.scrollDown
    : direction === 'left'
      ? styles.scrollLeft
      : styles.scrollRight;

  return (
    <div
      className={classNames(styles.wrapper, className)}
      role="marquee"
      aria-live="off"
    >
      <div
        className={classNames(styles.track, {
          [styles.vertical]: isVertical,
          [styles.paused]: isPaused,
        })}
        style={{
          animationDuration: `${calculatedDuration}s`,
          animationName,
        }}
      >
        <div
          className={classNames(styles.content, {
            [styles.vertical]: isVertical,
          })}
        >
          {items.map(({ name, state, sources }, itemIndex) => (
            <MarqueeItem
              key={`item-${itemIndex}`}
              index={itemIndex}
              name={name}
              state={state}
              sources={sources}
            />
          ))}
        </div>
        {/* Duplicate content for seamless looping */}
        <div
          className={classNames(styles.content, {
            [styles.vertical]: isVertical,
          })}
        >
          {items.map(({ name, state, sources }, itemIndex) => (
            <MarqueeItem
              key={`item-duplicate-${itemIndex}`}
              index={itemIndex}
              name={name}
              state={state}
              sources={sources}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
